
import React, { useState, useMemo, useEffect } from 'react';
import { userTradeService, UserTrade } from '../../services/user.trade.service';
import Button from '../../components/common/Button';
import Select from '../../components/common/Select';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  FileText,
  Activity,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Download,
  Target,
  Loader2
} from 'lucide-react';

interface ChartData {
  period: string;
  profit: number;
  loss: number;
}

const Reports: React.FC = () => {
  const [trades, setTrades] = useState<UserTrade[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [assetFilter, setAssetFilter] = useState('ALL');

  // Hardcoded archive for UI purposes as requested (until backend export API exists)
  const history = [
    { id: 'RPT-8271', name: 'Q1 Performance Audit', date: '2026-03-31', size: '2.4MB', status: 'READY' },
    { id: 'RPT-8272', name: 'Tax Compliance Export', date: '2026-02-15', size: '1.2MB', status: 'READY' },
    { id: 'RPT-8273', name: 'Settlement Ledger', date: '2026-01-10', size: '0.8MB', status: 'ARCHIVED' }
  ];

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await userTradeService.listTrades({});
      if (response.success) {
        setTrades(response.data);
      }
    } catch (error) {
      console.error('Analytics Fetch Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtered dataset
  const filteredTrades = useMemo(() => {
    if (assetFilter === 'ALL') return trades;
    return trades.filter(t => t.asset === assetFilter);
  }, [trades, assetFilter]);

  // Dynamic Summary Calculations
  const summary = useMemo(() => {
    const total = filteredTrades.length;
    const wins = filteredTrades.filter(t => t.outcome === 'PROFIT');
    const losses = filteredTrades.filter(t => t.outcome === 'LOSS');
    const totalInvested = filteredTrades.reduce((sum, t) => sum + parseFloat(t.principal || '0'), 0);
    const totalPayout = filteredTrades.reduce((sum, t) => sum + parseFloat(t.payout || '0'), 0);

    return {
      totalTrades: total,
      wins: wins.length,
      losses: losses.length,
      winRate: total > 0 ? ((wins.length / total) * 100).toFixed(1) : '0.0',
      totalInvestment: totalInvested,
      totalPayout: totalPayout
    };
  }, [filteredTrades]);

  // Chart Data Generation (Grouping by Month)
  const chartData = useMemo(() => {
    const groups: Record<string, ChartData> = {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Pre-populate last 6 months
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${months[d.getMonth()]} ${d.getFullYear().toString().slice(-2)}`;
      groups[key] = { period: key, profit: 0, loss: 0 };
    }

    filteredTrades.forEach(trade => {
      const date = new Date(trade.executedAt);
      const key = `${months[date.getMonth()]} ${date.getFullYear().toString().slice(-2)}`;

      if (groups[key]) {
        if (trade.outcome === 'PROFIT') {
          groups[key].profit += parseFloat(trade.payout || '0');
        } else if (trade.outcome === 'LOSS') {
          groups[key].loss += parseFloat(trade.principal || '0');
        }
      }
    });

    return Object.values(groups);
  }, [filteredTrades]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <Loader2 className="animate-spin text-brand-primary" size={48} />
        <p className="text-brand-muted text-[10px] font-black uppercase tracking-[0.3em] animate-pulse">Running Analytical Audit...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">

      {/* 1. Header & Quick Filters */}
      <section className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight uppercase">Analytical Ledger</h1>
          <p className="text-brand-muted text-xs font-bold uppercase tracking-widest opacity-70">Performance Analytics & fiscal data</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-brand-card border border-brand-border p-1 rounded-xl">
            <button className="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest bg-brand-primary text-black rounded-lg shadow-lg shadow-brand-primary/20">Real-Time</button>
            <button className="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-brand-muted hover:text-white transition-colors">Archive</button>
          </div>
        </div>
      </section>

      {/* 2. Trade Summary Performance Grid */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <SummaryCard label="Executions" value={summary.totalTrades} icon={<Activity size={16} />} />
        <SummaryCard label="Wins" value={summary.wins} icon={<TrendingUp size={16} />} color="text-emerald-500" />
        <SummaryCard label="Losses" value={summary.losses} icon={<TrendingDown size={16} />} color="text-red-500" />
        <SummaryCard label="Win Rate" value={`${summary.winRate}%`} icon={<Target size={16} />} color="text-brand-primary" />
        <SummaryCard label="Invested" value={`$${(summary.totalInvestment / 1000).toFixed(1)}k`} icon={<TrendingUp size={16} />} />
        <SummaryCard label="Total Payout" value={`$${(summary.totalPayout / 1000).toFixed(1)}k`} icon={<BarChart3 size={16} />} color="text-emerald-500" />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* 3. Performance Dynamics Chart */}
        <div className="lg:col-span-8 bg-brand-card border border-brand-border rounded-3xl p-8 shadow-xl">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h3 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
                <BarChart3 size={20} className="text-brand-primary" />
                Performance Dynamics
              </h3>
              <p className="text-[10px] text-brand-muted uppercase font-bold tracking-widest mt-1">Profit vs Deficit (Monthly Aggregation)</p>
            </div>
            <div className="flex items-center gap-4">
              <Select
                options={[
                  { value: 'ALL', label: 'All Protocols' },
                  { value: 'BTC', label: 'Bitcoin' },
                  { value: 'ETH', label: 'Ethereum' },
                  { value: 'USDT', label: 'Tether' }
                ]}
                value={assetFilter}
                onChange={(e) => setAssetFilter(e.target.value)}
                className="w-32 !py-2 !text-[10px]"
              />
            </div>
          </div>

          <div className="h-[350px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorLoss" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#262626" />
                <XAxis
                  dataKey="period"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#8E8E8E', fontSize: 10, fontWeight: 'bold' }}
                />
                <YAxis hide />
                <Tooltip
                  contentStyle={{ backgroundColor: '#141414', border: '1px solid #262626', borderRadius: '12px' }}
                  itemStyle={{ fontSize: '11px', fontWeight: 'bold' }}
                  formatter={(value: any) => [`$${parseFloat(value).toLocaleString()}`, '']}
                />
                <Area
                  type="monotone"
                  dataKey="profit"
                  name="Total ROI"
                  stroke="#10B981"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorProfit)"
                />
                <Area
                  type="monotone"
                  dataKey="loss"
                  name="Total Loss"
                  stroke="#EF4444"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorLoss)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 4. Report Document Archive (Sidebar) */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-brand-card border border-brand-border rounded-3xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Nexus Export Queue</h3>
              <Download size={16} className="text-brand-muted" />
            </div>
            <div className="space-y-4">
              {history.map(item => (
                <div key={item.id} className="p-4 bg-brand-dark/50 border border-brand-border rounded-2xl group hover:border-brand-primary/30 transition-all cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <FileText size={18} className="text-brand-muted group-hover:text-brand-primary transition-colors" />
                      <div>
                        <p className="text-xs font-bold text-white tracking-tight">{item.name}</p>
                        <p className="text-[9px] text-brand-muted uppercase font-bold mt-1 tracking-widest">{item.date} • {item.size}</p>
                      </div>
                    </div>
                    <span className={`text-[8px] font-black px-1.5 py-0.5 rounded ${item.status === 'READY' ? 'text-emerald-500 bg-emerald-500/10' : 'text-zinc-500 bg-zinc-500/10'}`}>
                      {item.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <button className="w-full mt-6 py-3 border border-brand-border rounded-xl text-[10px] font-black uppercase tracking-[0.2em] text-brand-muted hover:text-white hover:bg-white/5 transition-all">
              Load Older Records
            </button>
          </div>

          <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-3xl p-6">
            <h4 className="text-[10px] font-black text-brand-primary uppercase tracking-[0.2em] mb-3">Report Integrity</h4>
            <p className="text-[10px] text-brand-muted leading-relaxed uppercase font-bold tracking-wider opacity-80">
              All financial exports are cryptographically signed by the COINNEXS Nexus protocol to ensure tax compliance and data non-repudiation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryCard: React.FC<{ label: string, value: string | number, icon: React.ReactNode, color?: string }> = ({ label, value, icon, color = 'text-white' }) => (
  <div className="bg-brand-card border border-brand-border p-4 rounded-2xl flex items-center gap-4 hover:border-brand-primary/20 transition-all group">
    <div className="w-10 h-10 rounded-xl bg-brand-dark border border-brand-border flex items-center justify-center text-brand-muted group-hover:text-brand-primary transition-colors">
      {icon}
    </div>
    <div>
      <p className="text-[9px] text-brand-muted uppercase font-bold tracking-widest">{label}</p>
      <p className={`text-lg font-bold tracking-tighter ${color}`}>{value}</p>
    </div>
  </div>
);

export default Reports;
